import type { ReactNode } from 'react';
import { Box, Paper, Stack } from '@mui/material';
import wafLogo from '../../assets/waf-logo.png';

interface Props {
  children: ReactNode;
}

/**
 * Centered card layout shared by Login / PasswordReset / GettingStarted.
 * WAF-branded: yellow backdrop stripe at top, logo, card with content.
 *
 * Responsive notes:
 * - The card is a fluid 100% width up to maxWidth 440px, with larger padding
 *   on sm+ breakpoints.
 * - The WAF logo image is the wide festival banner, not a compact mark — it's
 *   constrained by width (not height) so it scales down on narrow viewports
 *   without bleeding past the card edge.
 */
export function AuthShell({ children }: Props) {
  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 4 },
        // Narrow yellow band at the very top, soft fade to the page bg so
        // the branded colour doesn't dominate the viewport while the form
        // sits centered.
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, #3a2d00 0%, #1a1300 18%, #0F1115 40%)'
            : 'linear-gradient(180deg, #FFCC00 0%, #FFE894 10%, #F4F5F7 35%)'
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 440,
          p: { xs: 3, sm: 5 },
          borderRadius: 2,
          // Guard against any stray child overflowing horizontally — some
          // MUI input adornments can push past the content box otherwise.
          overflow: 'hidden'
        }}
      >
        <Stack alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Box
            component="img"
            src={wafLogo}
            alt="WAF"
            sx={{
              // Fill the full width of the sign-in card (the logo is a wide
              // banner, so it spans the card and its height follows the
              // aspect ratio). No maxWidth cap — it should reach both edges
              // of the card's content area.
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
          />
        </Stack>
        {children}
      </Paper>
    </Box>
  );
}
